// Type-only imports: this file is asserted under plain node by `scripts/check-url-state.mjs`
// (no React, Apollo or router; a value import of another `.ts` file would not resolve there).
import type { ViewDef } from './requestChips';

// ── a token language, generic enough for FilterTokenBar to render any of them ──

export interface Token {
  key: string;
  value: string;
}

export interface Parsed {
  tokens: Token[];
  text: string;
}

/** Everything FilterTokenBar needs to render and edit one filter language. */
export interface FilterLanguage {
  parse(q: string): Parsed;
  serialize(f: Parsed): string;
  add(q: string, t: Token): string;
  remove(q: string, t: Token): string;
  keyLabel(key: string): string;
  valueLabel(t: Token): string;
  opLabel(t: Token): string;
  /** Keys offered by the "Add filter" menu, in menu order. */
  menuKeys: string[];
  values(key: string): string[];
  optionLabel(key: string, value: string): string;
  placeholder: string;
  /** False for a `key:value` the server cannot use (e.g. `workflow:change`). Absent = every token is valid. */
  isValid?(t: Token): boolean;
}

/**
 * The ONE filter language of the /tasks list (FilterTokenBar still falls
 * back to the access-only helpers in `requestChips.ts` when no `lang` is passed). The whole filter is one url param `q`, e.g.
 *
 *   state:open label:team::db label:blocked assignee:me due:soon redis
 *
 * Tokens map to the server's `tasks(filter: TaskFilterInput)`; everything that
 * is not a token is the server's free-text `q` (title, description, people, the
 * access target, label names, `#id`, `CR-n`).
 *
 * - `label:` is MULTI-valued (a task must carry all of them). A label name with
 *   spaces is written quoted: `label:"needs review"`.
 * - Every other key is single-valued: a second `state:` replaces the first.
 * - `workflow:todo|in-progress|done|none` goes to the server (`workflow:none` = open tasks with
 *   no `workflow::*` label). `priority:p1|p2|p3|none` is still understood in a URL, but it is not
 *   offered in the Add-filter menu: tasks no longer show a priority.
 * - `assignee:` matches ANY of a task's assignees (`assignee:me` = one of them is you).
 * - `awaiting:me` = open access tasks whose grant is pending and that YOU can decide —
 *   you are assigned, or root/maintainer (server `awaitingMe`; never your own task).
 */

export const REQUEST_STATES = ['open', 'closed'] as const;
export const ACCESS_STATES = ['any', 'pending', 'approved', 'denied', 'cancelled'] as const;
export const WORKFLOWS = ['todo', 'in-progress', 'done', 'none'] as const;
export const DUE_VALUES = ['overdue', 'soon'] as const;
export const DUE_SOON_DAYS = 7;
const DAY = 86_400_000;

/**
 * The one "soon" rule, shared by list rows, the `due:soon` filter and the detail
 * deadline banner: past due = over; due within 7×24h = soon; later otherwise.
 */
export function dueTone(dueAt: string, now: number): 'over' | 'soon' | 'later' | null {
  const t = new Date(dueAt).getTime();
  if (Number.isNaN(t)) return null;
  if (t < now) return 'over';
  return t - now <= DUE_SOON_DAYS * DAY ? 'soon' : 'later';
}

const KEYS: Record<string, { label: string; values: string[]; valueLabels?: Record<string, string>; op?: Record<string, string> }> = {
  state: { label: 'State', values: [...REQUEST_STATES], valueLabels: { open: 'Open', closed: 'Closed' } },
  label: { label: 'Label', values: [] },
  access: {
    label: 'Access',
    values: [...ACCESS_STATES],
    valueLabels: { any: 'any grant', pending: 'Pending', approved: 'Approved', denied: 'Denied', cancelled: 'Cancelled' },
    op: { any: 'has' },
  },
  assignee: { label: 'Assignee', values: ['me'] },
  requester: { label: 'Requester', values: ['me'] },
  workflow: {
    label: 'Workflow',
    values: [...WORKFLOWS],
    valueLabels: {
      todo: 'To do',
      'in-progress': 'In progress',
      done: 'Done',
      none: 'no workflow label',
    },
    op: { none: 'has' },
  },
  awaiting: { label: 'Waiting on', values: ['me'], valueLabels: { me: 'me' } },
  due: {
    label: 'Due',
    values: [...DUE_VALUES],
    valueLabels: { overdue: 'overdue', soon: `${DUE_SOON_DAYS} days` },
    op: { soon: 'within' },
  },
};
const MULTI = new Set(['label']);
/** Filter keys that no longer exist: a word using one is dropped, never searched as text. */
const RETIRED_KEYS = new Set(['priority']);

/** Words of a query; a `key:"quoted value"` stays one word. */
function words(q: string): string[] {
  return q.match(/[a-z]+:"[^"]*"?|\S+/gi) ?? [];
}

const unquote = (v: string) => v.replace(/^"/, '').replace(/"$/, '');
const quote = (v: string) => (/\s/.test(v) ? `"${v}"` : v);

function parse(q: string): Parsed {
  const tokens: Token[] = [];
  const text: string[] = [];
  for (const w of words(q)) {
    const i = w.indexOf(':');
    const k = i > 0 ? w.slice(0, i).toLowerCase() : '';
    const v = i > 0 ? unquote(w.slice(i + 1)).trim().toLowerCase() : '';
    if (RETIRED_KEYS.has(k)) continue;
    if (k in KEYS && v) {
      if (MULTI.has(k)) {
        if (!tokens.some((t) => t.key === k && t.value === v)) tokens.push({ key: k, value: v });
      } else {
        const at = tokens.findIndex((t) => t.key === k);
        if (at >= 0) tokens.splice(at, 1);
        tokens.push({ key: k, value: v });
      }
    } else text.push(w);
  }
  return { tokens, text: text.join(' ') };
}

function serialize(f: Parsed): string {
  return [...f.tokens.map((t) => `${t.key}:${quote(t.value)}`), f.text.trim()].filter(Boolean).join(' ');
}

const valueLabel = (t: Token) => KEYS[t.key]?.valueLabels?.[t.value] ?? t.value;

/** Keys whose value is free (a label name, an email): any non-empty value is valid. */
const FREE_VALUES = new Set(['label', 'assignee', 'requester']);

/**
 * Is this token's value one the server understands? `toRequestFilter` drops an invalid
 * token, so without this check `workflow:change` silently became "no filter" and the
 * list claimed every task matched.
 */
export function isValidRequestToken(t: Token): boolean {
  if (FREE_VALUES.has(t.key)) return t.value.trim() !== '';
  const v = t.key === 'workflow' && t.value === 'in_progress' ? 'in-progress' : t.value;
  return KEYS[t.key]?.values.includes(v) ?? false;
}

/** The tokens of a parsed query the server cannot use. Empty = the query is sendable. */
export function invalidRequestTokens(f: Parsed): Token[] {
  return f.tokens.filter((t) => !isValidRequestToken(t));
}

/**
 * Build the language. `labelNames` feeds the "Add filter → Label" menu (the
 * non-archived labels from the `labels` query); any typed `label:x` works without it.
 */
export function requestLanguage(labelNames: string[] = []): FilterLanguage {
  return {
    parse,
    serialize,
    add: (q, t) => {
      const f = parse(q);
      const v = t.value.trim().toLowerCase();
      const keep = f.tokens.filter((x) => (MULTI.has(t.key) ? !(x.key === t.key && x.value === v) : x.key !== t.key));
      return serialize({ tokens: [...keep, { key: t.key, value: v }], text: f.text });
    },
    remove: (q, t) => {
      const f = parse(q);
      return serialize({ tokens: f.tokens.filter((x) => !(x.key === t.key && x.value === t.value)), text: f.text });
    },
    keyLabel: (k) => KEYS[k]?.label ?? k,
    valueLabel,
    opLabel: (t) => KEYS[t.key]?.op?.[t.value] ?? 'is',
    menuKeys: Object.keys(KEYS),
    values: (k) => (k === 'label' ? labelNames.map((n) => n.toLowerCase()) : (KEYS[k]?.values ?? [])),
    optionLabel: (k, v) => valueLabel({ key: k, value: v }),
    placeholder: 'Search or filter… (e.g. state:open label:team::db assignee:me workflow:none)',
    isValid: isValidRequestToken,
  };
}

export const REQUEST_LANG = requestLanguage();

/** Order-insensitive form, for "is this saved view the active one?". */
export function normalizeRequestQuery(q: string): string {
  const f = parse(q);
  const tokens = [...f.tokens].sort((a, b) => a.key.localeCompare(b.key) || a.value.localeCompare(b.value));
  return serialize({ tokens, text: f.text.trim().replace(/\s+/g, ' ') });
}

// ── to the server ─────────────────────────────────────────────────────────────

/** Mirror of the api's `TaskFilterInput` (only the fields this page sends). */
export interface RequestServerFilter {
  state?: string[];
  template?: string[];
  access?: string[];
  labels?: string[];
  assignee?: string;
  requester?: string;
  dueBefore?: string;
  workflow?: string[];
  awaitingMe?: boolean;
  q?: string;
}

/**
 * Tokens → `TaskFilterInput`. `me` resolves `assignee:me` / `requester:me`.
 * `due:` implies open (a closed task is never overdue) unless `state:` says
 * otherwise. Unknown token values are dropped rather than sent (the api would
 * refuse the whole query).
 */
export function toRequestFilter(f: Parsed, me: string, now: number): RequestServerFilter {
  const out: RequestServerFilter = {};
  const who = (v: string) => (v === 'me' ? me.toLowerCase() : v);
  for (const t of f.tokens) {
    switch (t.key) {
      case 'state':
        if ((REQUEST_STATES as readonly string[]).includes(t.value)) out.state = [t.value];
        break;
      case 'access':
        if (t.value === 'any') out.template = ['access'];
        else if ((ACCESS_STATES as readonly string[]).includes(t.value)) out.access = [t.value];
        break;
      case 'label':
        (out.labels ??= []).push(t.value);
        break;
      case 'assignee':
        out.assignee = who(t.value);
        break;
      case 'requester':
        out.requester = who(t.value);
        break;
      case 'workflow': {
        const v = t.value === 'in_progress' ? 'in-progress' : t.value;
        if ((WORKFLOWS as readonly string[]).includes(v)) out.workflow = [v];
        break;
      }
      case 'awaiting':
        if (t.value === 'me') out.awaitingMe = true;
        break;
      case 'due':
        if (t.value === 'overdue') out.dueBefore = new Date(now).toISOString();
        else if (t.value === 'soon') out.dueBefore = new Date(now + DUE_SOON_DAYS * DAY).toISOString();
        if (out.dueBefore && !f.tokens.some((x) => x.key === 'state')) out.state = ['open'];
        break;
    }
  }
  const text = f.text.trim();
  if (text) out.q = text;
  return out;
}

// ── saved views ───────────────────────────────────────────────────────────────

/** Presets are named filters; the active tab is whichever the url matches.
 *  `reviewerOnly` = engineer and above: a new member sees access tasks only and can never
 *  be assigned or decide one, so those views would always be empty for them. */
export const REQUEST_VIEWS: ViewDef[] = [
  {
    slug: 'waiting-on-me',
    label: 'Waiting on me',
    q: 'awaiting:me',
    reviewerOnly: true,
  },
  { slug: 'open', label: 'Open', q: 'state:open' },
  { slug: 'assigned', label: 'Assigned to me', q: 'state:open assignee:me', reviewerOnly: true },
  { slug: 'mine', label: 'Mine', q: 'requester:me' },
  { slug: 'pending-access', label: 'Pending access', q: 'access:pending' },
  { slug: 'due-soon', label: 'Due soon', q: 'due:soon', reviewerOnly: true },
  { slug: 'all', label: 'All', q: '' },
];
